import { AutoAwesome } from "@mui/icons-material";
import { Button, ListItemIcon, MenuItem } from "@mui/material";
import { useMemo } from "react";
import { toast } from "../../../../utils/snackbarHelper";
import { toastMessage } from "../../../../constants/strings";
import { labelButtonStyle } from "../../../../components/shared/PopupMenu";
import {
  PlanTableFormat,
  PlanSummary,
} from "../../../../types/planning/plan.type";
import Loading from "../../../../components/shared/layout/Loading";
import { useAutoSequenceRoutes } from "../../../../hooks/react-query/useAutoSequenceRoute";
import { queryClient } from "../../../../queryClientProvider";
import { plannedRoutesQueryKey } from "../../../../hooks/react-query/usePlannedRoutes";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import { useDateSearchParamOrFallbackToToday } from "../../../../hooks/useDateSearchParamOrFallbackToToday";
import { dateToInt } from "../../../../utils/dateTimeHelper";
import { useInboundPlanningContext } from "../InboundPlanningContext";

type AutoSequenceMenuItem = {
  routesOrTraps: PlanTableFormat[];
  /** Omit if don't want to call it after the action */
  handleClose?: () => void;
};

export const AutoSequenceMenuItem = ({
  routesOrTraps,
  handleClose,
}: AutoSequenceMenuItem) => {
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(selectedDate);
  const {
    setAutoSequenceTimestamp,
    plannedRoutes,
    pendingAutoSequenceRouteIds,
  } = useInboundPlanningContext();

  const routes = routesOrTraps.filter(
    (record) => record.type === "delivery_route"
  );

  const { mutateAsync: autoSequenceRoutes } = useAutoSequenceRoutes();

  const isAutoSequenceRunning = useMemo(() => {
    const routeIds = new Set(routes.map((r) => r.id));
    const matchesPending = pendingAutoSequenceRouteIds.some((id) =>
      routeIds.has(id)
    );
    const matchesPlans = plannedRoutes.some(
      (route) =>
        routeIds.has(route.id) &&
        route.type === "delivery_route" &&
        route.sequenceStatus === "processing"
    );
    return matchesPending || matchesPlans;
  }, [routes, plannedRoutes, pendingAutoSequenceRouteIds]);

  const handleAutoSequenceRoutesClick = async () => {
    if (routes.length === 0) {
      toast(toastMessage.inbound.autoSequencing.selectAtLeastOneRoute, {
        variant: "warning",
      });
    } else {
      // Optimistically update UI to show "processing" state
      // This provides immediate feedback to the user
      queryClient.setQueryData(
        plannedRoutesQueryKey(planDate, serviceCenter.id),
        (prev: PlanSummary[]) =>
          prev.map((route) => {
            const routeStatus = routes.find((r) => route.id === r.id);
            if (!routeStatus) return route;

            return {
              ...route,
              sequenceStatus: "processing" as const,
              isManuallySequenced: false,
            };
          })
      );
      // Don't clear selection here - users should be able to perform follow-up actions
      handleClose?.();

      try {
        // CRITICAL: Call auto-sequencer API FIRST
        await autoSequenceRoutes(routes.map((route) => route.id));

        // Small delay to ensure backend has started processing before we start polling
        // This prevents the first poll from returning stale data before backend updates
        await new Promise((resolve) => setTimeout(resolve, 500));

        // THEN set timestamp to start polling status API
        // The polling hook checks for timestamp > 0, so it won't start until now
        setAutoSequenceTimestamp(new Date().getTime());
      } catch (error) {
        // Revert optimistic update on error
        queryClient.setQueryData(
          plannedRoutesQueryKey(planDate, serviceCenter.id),
          (prev: PlanTableFormat[]) =>
            prev.map((route) => {
              const routeStatus = routes.find((r) => route.id === r.id);
              if (!routeStatus) return route;

              return {
                ...route,
                sequenceStatus: "idle" as const,
              };
            })
        );

        // Error will be shown by the mutation's error handler
        throw error;
      }
    }
  };

  return (
    <MenuItem onClick={handleAutoSequenceRoutesClick}>
      <ListItemIcon>
        {isAutoSequenceRunning ? (
          <Loading size={16} containerWidth={16} />
        ) : (
          <AutoAwesome />
        )}
      </ListItemIcon>
      <Button sx={labelButtonStyle} disableRipple>
        {isAutoSequenceRunning ? "Auto-Sequencing..." : "Auto-Sequence"}
      </Button>
    </MenuItem>
  );
};
